import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useI18n } from "@/lib/i18n";
import { FadeUp } from "@/components/Reveal";

function Row({ p, i, tCta, tView, lang }) {
  const reverse = i % 2 === 1;
  const rowRef = useRef();
  const { scrollYProgress } = useScroll({ target: rowRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-20%", "20%"]);
  const numberY = useTransform(scrollYProgress, [0, 1], ["30%", "-30%"]);
  const nl = lang === "nl";
  const de = lang === "de";
  const L = {
    category: nl ? "Categorie" : de ? "Kategorie" : "Category",
    dosage: nl ? "Dosering" : de ? "Dosierung" : "Dosage",
    purity: nl ? "Zuiverheid" : de ? "Reinheit" : "Purity",
    stock: nl ? "Voorraad" : de ? "Lagerbestand" : "Stock",
    dispatch: nl ? "Verzending" : de ? "Versand" : "Dispatch",
    solvent: nl ? "Oplosmiddel" : de ? "Lösungsmittel" : "Solvent",
    peptide: nl ? "Peptide · Flacon" : de ? "Peptid · Fläschchen" : "Peptide · Vial",
    pen: nl ? "Peptide · Pen" : de ? "Peptid · Pen" : "Peptide · Pen",
    stockLow: (n) => nl ? `Nog ${n} beschikbaar` : de ? `Noch ${n} verfügbar` : `${n} left`,
    stockHigh: (n) => nl ? `${n}+ op voorraad` : de ? `${n}+ auf Lager` : `${n}+ in stock`,
    dispatchNote: nl ? "Vandaag als besteld voor 15:00" : de ? "Heute wenn bis 15:00 bestellt" : "Today when ordered before 3PM",
    priceLbl: nl ? "Prijs" : de ? "Preis" : "Price",
  };
  const catLabel = p.category === "bac_water" ? L.solvent : p.category === "peptide_pen" ? L.pen : p.category === "accessory" ? (nl ? "Accessoire" : de ? "Zubehör" : "Kit") : L.peptide;
  const specs = p.category === "accessory"
    ? [
        [L.category, catLabel],
        [nl ? "Bevat" : de ? "Enthält" : "Contains", nl ? "1× etui · 12× naalden · 10× doekjes" : de ? "1× Etui · 12× Nadeln · 10× Tücher" : "1× case · 12× needles · 10× wipes"],
        [L.stock, p.stock <= 10 ? L.stockLow(p.stock) : L.stockHigh(p.stock)],
        [L.dispatch, L.dispatchNote],
      ]
    : [
        [L.category, catLabel],
        [L.dosage, p.dosage],
        [L.purity, "≥ 99% (HPLC)"],
        [L.stock, p.stock <= 10 ? L.stockLow(p.stock) : L.stockHigh(p.stock)],
        [L.dispatch, L.dispatchNote],
      ];

  return (
    <div ref={rowRef} data-testid={`overview-row-${p.slug}`} className="relative py-24 md:py-32 border-b border-neutral-300 overflow-hidden">
      <div className={`max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
        {/* Image side */}
        <div className="lg:col-span-7 relative">
          <motion.div
            className="absolute pointer-events-none inset-0"
            style={{
              background: `radial-gradient(circle at 50% 50%, ${p.accent}22, transparent 60%)`,
              filter: "blur(30px)",
            }}
          />
          <motion.div
            style={{ y: numberY }}
            className="absolute -top-8 left-0 lg:-left-4 font-serif-display italic text-[220px] md:text-[320px] leading-none text-neutral-200/70 select-none pointer-events-none"
          >
            0{i + 1}
          </motion.div>
          <motion.div style={{ y }} className="relative z-10">
            <div className="spotlight aspect-[4/5] flex items-center justify-center">
              <motion.img
                src={p.image_url}
                alt={p.name}
                className="max-h-[560px] w-auto object-contain"
                style={{
                  filter: `drop-shadow(0 40px 60px ${p.accent}55) drop-shadow(0 10px 20px rgba(0,0,0,0.12))`,
                }}
                whileHover={{ scale: 1.04, rotate: reverse ? -1 : 1 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              />
            </div>
          </motion.div>
        </div>

        {/* Content side */}
        <div className="lg:col-span-5 lg:pl-8 relative z-10">
          <FadeUp>
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono-label text-[10px] text-neutral-500">CH · 0{i + 1}</span>
              <span className="h-px flex-1" style={{ background: p.accent, opacity: 0.35 }} />
              <span className="font-mono-label text-[10px]" style={{ color: p.accent }}>
                {p.category === "bac_water"
                  ? (lang === "nl" ? "OPLOSMIDDEL" : lang === "de" ? "LÖSUNGSMITTEL" : "SOLVENT")
                  : p.category === "peptide_pen"
                  ? "PEN"
                  : p.category === "accessory"
                  ? (lang === "nl" ? "ACCESSOIRE" : lang === "de" ? "ZUBEHÖR" : "KIT")
                  : (lang === "nl" ? "FLACON" : lang === "de" ? "FLÄSCHCHEN" : "VIAL")}
              </span>
            </div>
            <h3 className="font-serif-display font-light text-6xl md:text-7xl leading-[0.95] tracking-tight">
              {p.name}
            </h3>
            <p className="font-serif-display italic text-3xl md:text-4xl text-neutral-500 mt-1">
              {p.dosage}
            </p>
          </FadeUp>

          <FadeUp delay={0.15}>
            <p className="mt-8 text-neutral-600 leading-relaxed max-w-lg">
              {lang === "nl" ? (p.short_desc_nl || p.short_desc_en) : (p.short_desc_en || p.short_desc_nl)}
            </p>
          </FadeUp>

          <FadeUp delay={0.25}>
            <div className="mt-8 border-t border-neutral-300">
              {specs.map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-neutral-300 py-2.5 font-mono-label text-[10px]">
                  <span className="text-neutral-500">{k}</span>
                  <span className="text-neutral-800">{v}</span>
                </div>
              ))}
            </div>
          </FadeUp>

          <FadeUp delay={0.35}>
            <div className="mt-8 flex items-end justify-between">
              <div>
                <p className="font-mono-label text-[10px] text-neutral-500">{L.priceLbl}</p>
                <p className="font-serif-display text-5xl">€{Number(p.price).toFixed(2)}</p>
              </div>
              <Link
                to={`/product/${p.slug}`}
                data-testid={`overview-cta-${p.slug}`}
                className="btn-sharp"
              >
                {tView} →
              </Link>
            </div>
          </FadeUp>
        </div>
      </div>
    </div>
  );
}

export default function ProductOverview({ products }) {
  const { t, lang } = useI18n();
  if (!products?.length) return null;
  return (
    <section data-testid="product-overview" className="bg-white">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-12 pt-12 md:pt-32 pb-6 md:pb-8">
        <div className="grid md:grid-cols-12 gap-4 md:gap-8">
          <div className="md:col-span-3">
            <p className="font-mono-label text-[10px] md:text-[11px] text-neutral-500">[ 02 · {t("sec_catalog_over").toUpperCase()} ]</p>
          </div>
          <div className="md:col-span-9">
            <FadeUp>
              <h2 className="font-serif-display font-light text-3xl sm:text-4xl md:text-7xl leading-[1.02] md:leading-[0.98] tracking-tight">
                {t("sec_catalog_title")}
              </h2>
              <p className="mt-3 md:mt-6 max-w-2xl text-sm md:text-base text-neutral-600">{t("sec_catalog_sub")}</p>
            </FadeUp>
          </div>
        </div>
      </div>

      {/* Mobile compact catalog — 2×2 grid, fits in one glance */}
      <div data-testid="product-overview-mobile" className="md:hidden border-t border-neutral-300">
        <div className="grid grid-cols-2 gap-0">
          {products.map((p, i) => (
            <Link
              key={p.id}
              to={`/product/${p.slug}`}
              data-testid={`overview-mobile-card-${p.slug}`}
              className="relative bg-white p-3 border-r border-b border-neutral-300 [&:nth-child(2n)]:border-r-0 flex flex-col active:bg-neutral-50 transition"
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-mono-label text-[8px] text-neutral-500">0{i + 1}</span>
                <span className="font-mono-label text-[8px]" style={{ color: p.accent }}>
                  {p.category === "bac_water"
                    ? (lang === "nl" ? "OPLOSMIDDEL" : lang === "de" ? "LÖSUNGSMITTEL" : "SOLVENT")
                    : p.category === "peptide_pen"
                    ? "PEN"
                    : p.category === "accessory"
                    ? (lang === "nl" ? "KIT" : lang === "de" ? "SET" : "KIT")
                    : (lang === "nl" ? "FLACON" : lang === "de" ? "FLÄSCHCHEN" : "VIAL")}
                </span>
              </div>
              <div
                className="relative aspect-square flex items-center justify-center overflow-hidden"
                style={{
                  background: `radial-gradient(circle at 50% 60%, ${p.accent}18, transparent 65%)`,
                }}
              >
                <img
                  src={p.image_url}
                  alt={p.name}
                  className="max-h-[110px] w-auto object-contain"
                  style={{ filter: `drop-shadow(0 12px 18px ${p.accent}55)` }}
                />
              </div>
              <h3 className="font-serif-display text-lg leading-tight mt-2 line-clamp-1">
                {p.name}
              </h3>
              <p className="font-serif-display italic text-xs text-neutral-500 leading-tight">
                {p.dosage}
              </p>
              <div className="mt-1.5 flex items-end justify-between">
                <span className="font-serif-display text-xl leading-none">€{Number(p.price).toFixed(0)}</span>
                <span className="font-mono-label text-[9px] tracking-[0.15em] text-black underline underline-offset-2">
                  {t("view_product")} →
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Desktop editorial rows */}
      <div className="hidden md:block border-t border-neutral-300">
        {products.map((p, i) => (
          <Row key={p.id} p={p} i={i} tCta={t("add_to_cart")} tView={t("view_product")} lang={lang} />
        ))}
      </div>
    </section>
  );
}
